import { useMemo } from 'react';
import { BusinessAccess } from './useBusinessAccessInvitations';
import { useAccessTypeLabelMap, useAccessStatusLabelMap } from './useAccessTypeOptions';
import {
  groupByAccessType,
  groupByStatus,
  calculateConversionRate,
  calculateAverageGuests,
  getTimeSeriesData,
  getHeatmapData,
  getTopGuests,
  groupByChannel,
  calculatePercentageChange,
} from '../utils/dashboardUtils';
import { PASS_STATUS } from '../pages/BusinessPage/activity';

export interface DashboardMetrics {
  totalAccess: number;
  pendingAccess: number;
  approvedAccess: number;
  inAccess: number;
  outAccess: number;
  rejectedAccess: number;
  cancelledAccess: number;
  conversionRate: number;
  averageGuests: number;
  accessTypeDistribution: ReturnType<typeof groupByAccessType>;
  statusDistribution: ReturnType<typeof groupByStatus>;
  channelDistribution: ReturnType<typeof groupByChannel>;
  topGuests: ReturnType<typeof getTopGuests>;
  timeSeriesData: ReturnType<typeof getTimeSeriesData>;
  heatmapData: ReturnType<typeof getHeatmapData>;
}

export interface ComparisonMetrics {
  totalChange: number;
  pendingChange: number;
  inChange: number;
  conversionRateChange: number;
}

/**
 * Hook para calcular métricas del dashboard
 */
export const useDashboardMetrics = (
  data: BusinessAccess[],
  previousData?: BusinessAccess[],
  granularity: 'hour' | 'day' | 'week' | 'month' = 'day'
): { metrics: DashboardMetrics; comparison?: ComparisonMetrics } => {
  const { map: accessTypeLabelMap } = useAccessTypeLabelMap();
  const { map: accessStatusLabelMap } = useAccessStatusLabelMap();

  const metrics = useMemo(() => {
    const totalAccess = data.length;
    // Status ids as the backend has them (PASS_STATUS): 4 Checked In, 5 Checked Out, 6 Cancelled. This read the old
    // mapping (3 in, 6 out, 5 cancelled, 4 rejected), so «Currently In» stayed 0 with passes checked in (SAM-1321).
    const countStatus = (id: number) => data.filter(item => item.business_access_status_id === id).length;
    const pendingAccess = countStatus(PASS_STATUS.pending);
    const approvedAccess = countStatus(PASS_STATUS.approved);
    const inAccess = countStatus(PASS_STATUS.in);
    const outAccess = countStatus(PASS_STATUS.out);
    // There is no «rejected» pass status; the field stays for the shape of DashboardMetrics.
    const rejectedAccess = 0;
    const cancelledAccess = countStatus(PASS_STATUS.cancelled);

    return {
      totalAccess,
      pendingAccess,
      approvedAccess,
      inAccess,
      outAccess,
      rejectedAccess,
      cancelledAccess,
      conversionRate: calculateConversionRate(data),
      averageGuests: calculateAverageGuests(data),
      accessTypeDistribution: groupByAccessType(data, accessTypeLabelMap),
      statusDistribution: groupByStatus(data, accessStatusLabelMap),
      channelDistribution: groupByChannel(data),
      topGuests: getTopGuests(data, 10, accessTypeLabelMap),
      timeSeriesData: getTimeSeriesData(data, granularity),
      heatmapData: getHeatmapData(data),
    };
  }, [data, granularity, accessTypeLabelMap, accessStatusLabelMap]);

  const comparison = useMemo(() => {
    if (!previousData || previousData.length === 0) return undefined;

    const prevTotal = previousData.length;
    const prevPending = previousData.filter(item => item.business_access_status_id === PASS_STATUS.pending).length;
    const prevIn = previousData.filter(item => item.business_access_status_id === PASS_STATUS.in).length;
    const prevConversionRate = calculateConversionRate(previousData);

    return {
      totalChange: calculatePercentageChange(metrics.totalAccess, prevTotal),
      pendingChange: calculatePercentageChange(metrics.pendingAccess, prevPending),
      inChange: calculatePercentageChange(metrics.inAccess, prevIn),
      conversionRateChange: calculatePercentageChange(metrics.conversionRate, prevConversionRate),
    };
  }, [previousData, metrics]);

  return { metrics, comparison };
};
